<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Loader2, Trash2 } from "@lucide/vue";
import { useModalStore } from "@/stores/modals/modals.store";

/**
 * The app's one delete confirmation, opened through `modals.open("DeleteModal", props)` and resolving
 * the store's promise with `{ action: "confirm" | "cancel" }`. Red is the semantic here, not decoration:
 * the tile and the confirm button are the only red on screen, so the reader knows which button saves
 * nothing. Cancel takes focus first, so Enter on arrival never deletes.
 *
 * With `onConfirm` the dialog does the waiting: it stays open with the confirm button busy while the
 * caller's work runs, then closes with `{ action: "confirm" }` — the caller handles its own errors
 * and toasts inside `onConfirm`. Without it, confirming closes at once, as before.
 */
const props = withDefaults(
  defineProps<{
    heading?: string;
    itemName?: string;
    itemType?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    /** What the confirm button says while `onConfirm` runs. */
    busyLabel?: string;
    /** The deletion itself, awaited with the dialog open and busy. */
    onConfirm?: () => Promise<unknown>;
  }>(),
  {
    confirmLabel: "Delete",
    cancelLabel: "Cancel",
    busyLabel: "Deleting…",
    onConfirm: undefined,
  }
);

const modals = useModalStore();

const resolvedHeading = computed(() => {
  if (props.heading) return props.heading;
  if (props.itemType) return `Delete ${props.itemType}?`;
  return "Are you sure?";
});

const resolvedMessage = computed(() => {
  if (props.message) return props.message;
  if (props.itemName) return `Are you sure you want to delete "${props.itemName}"? This action cannot be undone.`;
  return "This action cannot be undone.";
});

const cancelButton = ref<HTMLButtonElement | null>(null);
onMounted(() => cancelButton.value?.focus());

const buttonBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";

const isDeleting = ref(false);

const handleConfirm = async () => {
  if (isDeleting.value) return;
  if (!props.onConfirm) {
    modals.closeByName("DeleteModal", { action: "confirm" });
    return;
  }
  isDeleting.value = true;
  modals.setBusy("DeleteModal", true);
  try {
    await props.onConfirm();
  } finally {
    isDeleting.value = false;
    modals.setBusy("DeleteModal", false);
    modals.closeByName("DeleteModal", { action: "confirm" });
  }
};

const handleCancel = () => {
  if (isDeleting.value) return;
  modals.closeByName("DeleteModal", { action: "cancel" });
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex items-start gap-4">
      <span class="bg-app-red-100 rounded-8 flex h-10 w-10 shrink-0 items-center justify-center">
        <Trash2
          class="icon-md text-app-red-600"
          :stroke-width="1.5"
          aria-hidden="true" />
      </span>
      <div class="flex min-w-0 flex-col gap-1 pt-0.5">
        <h2 class="t-sh4 text-type-default">{{ resolvedHeading }}</h2>
        <p class="t-small text-type-muted max-w-prose">{{ resolvedMessage }}</p>
      </div>
    </div>

    <div class="gap-px-8 flex justify-end">
      <button
        ref="cancelButton"
        type="button"
        :aria-disabled="isDeleting || undefined"
        :class="[
          buttonBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isDeleting },
        ]"
        @click="handleCancel">
        {{ cancelLabel }}
      </button>
      <button
        type="button"
        :aria-disabled="isDeleting || undefined"
        :aria-busy="isDeleting || undefined"
        :class="[
          buttonBase,
          'bg-app-red-600 dark:bg-app-red-fill hover:bg-app-red-700 dark:hover:bg-app-red-fill-hover text-white',
          { 'interactive-disabled': isDeleting },
        ]"
        @click="handleConfirm">
        <Loader2
          v-if="isDeleting"
          class="icon-sm animate-spin"
          :stroke-width="1.5"
          aria-hidden="true" />
        {{ isDeleting ? busyLabel : confirmLabel }}
      </button>
    </div>
  </div>
</template>
